import { useEffect } from 'react';

interface UseLessonAutoScrollProps {
  scrollContentRef: React.RefObject<HTMLDivElement>;
  currentInteractionIndex: number;
  isGreetingDone: boolean;
  loading: boolean;
  interactions: any[];
  showInteractions: boolean;
  interactionStates: Record<string, any>;
  chatThreads: Record<string, any[]>;
  agentIsLoading: boolean;
  showCurtain: boolean;
  activeQuizResponseItemId: string | null;
}

export const useLessonAutoScroll = ({
  scrollContentRef,
  currentInteractionIndex,
  isGreetingDone,
  loading,
  interactions,
  showInteractions,
  interactionStates,
  chatThreads,
  agentIsLoading,
  showCurtain,
  activeQuizResponseItemId
}: UseLessonAutoScrollProps) => {
  useEffect(() => {
    const content = scrollContentRef.current;
    if (!content) return;
    const container = content.parentElement;
    if (!container) return;

    const autoScroll = () => {
      const currentItem = interactions[currentInteractionIndex];
      const hasChat = currentItem && chatThreads[currentItem.id] && chatThreads[currentItem.id].length > 0;
      const currentItemState = interactionStates[currentItem?.id] || {};
      const isQuizAnswered = currentItem?.type === 'quiz' && currentItemState.isAnswered;
      const isRewarded = currentItemState.isRewarded;

      const behavior = showCurtain ? 'auto' : 'smooth';

      // 1. If we are in a chat interaction (user asked something or agent is responding), 
      // OR if a quiz was answered (to show feedback banner and action buttons), 
      // OR if a reward is being displayed (e.g. video completion), scroll to bottom
      if (activeQuizResponseItemId || hasChat || agentIsLoading || isQuizAnswered || isRewarded) {
        container.scrollTo({ top: container.scrollHeight, behavior });
        return;
      }

      // 2. Otherwise, for lesson steps, scroll to the top of the interaction container
      // This ensures Step Title and Content "come together" and stay at the top.
      const interactionElements = content.querySelectorAll('.v2-play-lesson-interaction');
      if (interactionElements.length > 0) {
        const lastInteraction = interactionElements[interactionElements.length - 1] as HTMLElement;
        // Scroll to interaction top with a small offset for the header (e.g., 12px)
        container.scrollTo({
          top: lastInteraction.offsetTop - 12,
          behavior
        });
      } else {
        // Handle initial greeting or loading state
        const msgWrappers = content.querySelectorAll('.v2-play-lesson-msg-wrapper');
        if (msgWrappers.length > 0) {
          const firstMsg = msgWrappers[0] as HTMLElement;
          container.scrollTo({ top: firstMsg.offsetTop - 12, behavior });
        } else {
          container.scrollTo({ top: container.scrollHeight, behavior });
        }
      }
    };

    const resizeObserver = new ResizeObserver(autoScroll);
    resizeObserver.observe(content);
    autoScroll();
    return () => resizeObserver.disconnect();
  }, [
    scrollContentRef,
    currentInteractionIndex,
    isGreetingDone,
    loading,
    interactions,
    showInteractions,
    interactionStates,
    chatThreads,
    agentIsLoading,
    showCurtain,
    activeQuizResponseItemId
  ]);
};
